import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * MEJORA 4: Unread service con persistencia en base de datos.
 *
 * Comportamiento:
 * - Al inicializar (login): carga el estado desde GET /conversations/unread-state
 * - Cuando llega un mensaje nuevo: incrementa el contador local
 * - Cuando el agente abre el chat: llama POST /conversations/:id/read y emite socket
 * - Cuando otro agente lee: el socket 'conversation_read' lo notifica y se actualiza aquí
 * - "Marcar como no leído" (clic derecho): llama POST /conversations/:id/mark-unread
 *
 * Valores de unreadCount:
 *  > 0  → número de mensajes no leídos del cliente
 *   0   → leído (sin badge)
 */
@Injectable({ providedIn: 'root' })
export class UnreadService {
    private readonly api = environment.apiUrl;

    /** Map<conversationId, unreadCount>  (0 = leído) */
    private state = new BehaviorSubject<Map<number, number>>(new Map());
    state$ = this.state.asObservable();

    constructor(private http: HttpClient) { }

    // ---------------------------------------------------------------------------
    // Initialization — call once after login
    // ---------------------------------------------------------------------------

    /** Carga el estado de no leídos desde la API (persiste entre sesiones). */
    loadFromApi(onComplete?: () => void): void {
        this.http.get<{ conversationId: number; unreadCount: number }[]>(
            `${this.api}/conversations/unread-state`
        ).subscribe({
            next: (items) => {
                const map = new Map<number, number>();
                items.forEach(({ conversationId, unreadCount }) => {
                    if (unreadCount !== 0) map.set(conversationId, unreadCount);
                });
                this.state.next(map);
                // After loading, let the caller clean up any convs that are already open
                onComplete?.();
            },
            error: (err) => console.warn('[UnreadService] Could not load unread state:', err)
        });
    }

    // ---------------------------------------------------------------------------
    // Local state helpers
    // ---------------------------------------------------------------------------

    hasUnread(convId: number): boolean {
        const v = this.state.value.get(convId);
        return v !== undefined && v !== 0;
    }

    getCount(convId: number): number {
        return this.state.value.get(convId) ?? 0;
    }

    /** Total de conversaciones con algún no leído (para el badge en la bandeja). */
    totalUnread(ids: number[]): number {
        return ids.filter(id => this.hasUnread(id)).length;
    }

    // ---------------------------------------------------------------------------
    // Increment — called when a new message arrives via socket and chat is NOT open
    // ---------------------------------------------------------------------------

    increment(convId: number): void {
        const map = new Map(this.state.value);
        const current = map.get(convId) ?? 0;
        map.set(convId, current + 1);
        this.state.next(map);
    }

    // ---------------------------------------------------------------------------
    // Mark read — persists to DB + updates local state
    // ---------------------------------------------------------------------------

    /**
     * Última vez que se envió el POST /read de cada conversación.
     * Abrir un chat dispara markRead desde varios sitios a la vez (el
     * NavigationEnd de la bandeja y el setConversationId de la ventana de chat),
     * y cada uno lanzaba su propia petición idéntica.
     */
    private ultimoMarcado = new Map<number, number>();
    private readonly VENTANA_DEDUPE_MS = 3000;

    markRead(convId: number): void {
        // Optimistic UI update
        const map = new Map(this.state.value);
        map.delete(convId);
        this.state.next(map);

        // El estado local siempre se actualiza (es gratis); lo que se evita es
        // repetir la petición. Saltarse un duplicado inmediato no afecta al
        // last_read_at: la llamada que sí sale ya lo deja al día.
        const ahora = Date.now();
        const previo = this.ultimoMarcado.get(convId);
        if (previo !== undefined && ahora - previo < this.VENTANA_DEDUPE_MS) {
            return;
        }
        this.ultimoMarcado.set(convId, ahora);

        // Persist to backend (backend also emits socket to all agents)
        this.http.post(`${this.api}/conversations/${convId}/read`, {}).subscribe({
            error: (err) => {
                // Si falló, permitir reintento inmediato en la siguiente llamada.
                this.ultimoMarcado.delete(convId);
                console.warn('[UnreadService] markRead failed:', err);
            }
        });
    }

    // ---------------------------------------------------------------------------
    // Mark unread manually (right-click → "Marcar como no leído")
    // ---------------------------------------------------------------------------

    markUnread(convId: number): void {
        // Optimistic UI update (assume it turns to 1 temporarily until socket realigns it)
        const map = new Map(this.state.value);
        if (!map.has(convId)) {
            map.set(convId, 1);
        }
        this.state.next(map);

        // Persist to backend (backend also emits socket)
        this.http.post(`${this.api}/conversations/${convId}/mark-unread`, {}).subscribe({
            error: (err) => console.warn('[UnreadService] markUnread failed:', err)
        });
    }

    // ---------------------------------------------------------------------------
    // Socket event handler — called from InboxComponent when 'conversation_read' arrives
    // ---------------------------------------------------------------------------

    /**
     * Handles the global socket event { conversationId, unreadCount }.
     */
    applySocketRead(payload: { conversationId: number; unreadCount: number }): void {
        const { conversationId, unreadCount } = payload;
        const map = new Map(this.state.value);

        if (unreadCount === 0) {
            map.delete(conversationId);
        } else {
            map.set(conversationId, unreadCount);
        }

        this.state.next(map);
    }

    /** Force-clear a conversation (used when chat window opens and we call markRead). */
    clearLocal(convId: number): void {
        const map = new Map(this.state.value);
        map.delete(convId);
        this.state.next(map);
    }
}
